import { useQuery } from "@tanstack/react-query";

import { api } from "./client";

export interface SummaryPoint {
  bucket: string;       // ISO date
  service: string;
  calls: number;
  tokens_in: number;
  tokens_out: number;
  cost_micros: number;
  errors: number;
}
export interface SummaryOut {
  points: SummaryPoint[];
}

export interface ReanalyzePoint {
  day: string;
  user_id: number;
  email: string | null;
  count: number;
}
export interface ReanalyzeOut {
  points: ReanalyzePoint[];
}

export interface TierPoint {
  date: string;
  user_id: number;
  email: string | null;
  live: number;
  tracked: number;
  idle: number;
}
export interface TiersOut {
  points: TierPoint[];
}

export interface OutlierRow {
  user_id: number;
  email: string | null;
  calls: number;
  tokens_in: number;
  tokens_out: number;
  cost_micros: number;
}
export interface OutliersOut {
  rows: OutlierRow[];
}

export function useTelemetryServices() {
  return useQuery<{ services: string[] }>({
    queryKey: ["telemetry", "services"],
    queryFn: async () =>
      (await api.get<{ services: string[] }>("/telemetry/services")).data,
    staleTime: 5 * 60_000,
  });
}

export function useTelemetrySummary(
  days: number,
  services: string[],
  granularity: "day" | "week",
) {
  const svcParam = services.join(",");
  return useQuery<SummaryOut>({
    queryKey: ["telemetry", "summary", days, svcParam, granularity],
    queryFn: async () =>
      (
        await api.get<SummaryOut>("/telemetry/summary", {
          params: {
            days,
            granularity,
            ...(svcParam ? { services: svcParam } : {}),
          },
        })
      ).data,
    staleTime: 60_000,
  });
}

export function useTelemetryReanalyze(days: number) {
  return useQuery<ReanalyzeOut>({
    queryKey: ["telemetry", "reanalyze", days],
    queryFn: async () =>
      (await api.get<ReanalyzeOut>("/telemetry/reanalyze", { params: { days } })).data,
    staleTime: 60_000,
  });
}

export function useTelemetryTiers(days: number) {
  return useQuery<TiersOut>({
    queryKey: ["telemetry", "tiers", days],
    queryFn: async () =>
      (await api.get<TiersOut>("/telemetry/tiers", { params: { days } })).data,
    staleTime: 60_000,
  });
}

export function useTelemetryOutliers(days: number) {
  return useQuery<OutliersOut>({
    queryKey: ["telemetry", "outliers", days],
    queryFn: async () =>
      (await api.get<OutliersOut>("/telemetry/outliers", { params: { days } })).data,
    staleTime: 60_000,
  });
}
